import 'Styles/ManagePortalUsers.scss';
import { useUserData } from 'Foundation/User/UserContext';
import { useEffect, useRef, useState } from 'react';
import { CreatePortalUser } from './CreatePortalUser';
import { PortalUsersTable } from './PortalUsersTable';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { Heading } from '@lmig/lmds-react-typography';
import { Notification } from '@lmig/lmds-react-notification';
import { IconSaveModern, IconRefresh } from '@lmig/lmds-react-icons';
import { Button } from '@lmig/lmds-react-button';
import { FieldGroup } from '@lmig/lmds-react-field-group';
import { SearchInput } from '@lmig/lmds-react-alphanumeric-input';
import { GetAgencyInfo } from './AgencyAPI';
import { sortBy } from 'lodash';
import { Modal, ModalBody, ModalFooter } from '@lmig/lmds-react-modal';
import { GridCol, GridRow } from '@lmig/lmds-react-grid';
import { Form } from '@lmig/lmds-react-form';
import ExportManagePortalUsersUtility from 'Utilities/ExportManagePortalUsersUtility';

function ManageBLPortalUsers() {
  const { userData } = useUserData();
  const [agencyCode, setAgencyCode] = useState<string | undefined>(undefined);
  const [agencyName, setAgencyName] = useState<string | undefined>(undefined);
  const [searchAgencyCode, setSearchAgencyCode] = useState<string | undefined>(undefined);
  const [isValidAgencySearch, setIsValidAgencySearch] = useState<boolean>(true);
  const [portalUsers, setPortalUsers] = useState<{ data?: IPortalUser[]; isLoaded: boolean; isAuthorized: boolean }>({
    isLoaded: false,
    isAuthorized: false,
  });
  const [portalUserCount, setPortalUserCount] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25 });
  const [sorting, setSorting] = useState({ sortBy: 'lastName', sortOrder: 'asc' as 'asc' | 'desc' });
  const [filters, setFilters] = useState<Record<string, string | string[]>>({});
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [hasLoadedAgency, setHasLoadedAgency] = useState<boolean>(false);
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState<boolean>(false);
  const [user, setUser] = useState<string>();
  const [isDisabling, setIsDisabling] = useState<boolean | undefined>();
  const [isUpdating, setIsUpdating] = useState<boolean | undefined>();
  const [initializeUserID, setInitializeUserID] = useState<string | undefined>(undefined);
  const [initializeUserIDError, setInitializeUserIDError] = useState<boolean | undefined>(false);
  const [noUsersFound, setNoUsersFound] = useState<boolean | undefined>(undefined);
  const [hasPerformedSearch, setHasPerformedSearch] = useState<boolean>(false);
  const exportUtility: any = ExportManagePortalUsersUtility.getInstance();
  const [notifications, setNotifications] = useState<
    { message: string; highlightType: 'positive' | 'negative'; index: number }[]
  >([]);
  const componentTopDiv = useRef<HTMLDivElement>(null);
  const AgencyCodeLength = 7;
  const validAgencyCode = new RegExp('^\\d{' + AgencyCodeLength + '}$');

  useEffect(() => {
    if (agencyCode) {
      setPagination({ page: 1, pageSize: 25 });
      setFilters({});
      setHasLoadedAgency(false);
      GetPortalUsers(agencyCode, portalUsers ? portalUsers.isAuthorized : false, false);
      GetAgencyName(agencyCode);
    }
  }, [agencyCode]);

  useEffect(() => {
    if (agencyCode && portalUsers.isLoaded) {
      GetPortalUsers(agencyCode, portalUsers.isAuthorized, true);
    }
  }, [pagination, sorting, filters]);

  useEffect(() => {
    if (userData && isAgentExperience()) {
      let statCode = userData.selectedStatCodeData?.FormattedSubStat;
      if (!statCode || (statCode.length === 0 && userData.selectedStatCodeData?.SubStat))
        statCode = userData.selectedStatCodeData?.SubStat; //fall back on substat if formatted sub is blank
      if (statCode && statCode.length > 0) {
        setAgencyCode(statCode);
        setAgencyName(userData.selectedStatCodeData.AgencyName);
      }
    } else if (userData && !isAgentExperience()) {
      GetPortalUsers('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.selectedStatCodeData?.FormattedSubStat, userData?.selectedStatCodeData?.SubStat]);

  useEffect(() => {
    if (portalUsers && portalUsers.data) {
      setPortalUserCount(portalUsers.data.length);
    } else {
      setPortalUserCount(0);
    }
  }, [portalUsers]);

  const SortPortalUsersByLastName = (ascending: boolean, portalUsers: IPortalUser[]): IPortalUser[] => {
    if (ascending !== undefined && portalUsers !== undefined && portalUsers.length > 0) {
      let tempPortalUsers: IPortalUser[] = portalUsers;
      tempPortalUsers = ascending
        ? sortBy(tempPortalUsers, [
            (user: IPortalUser) => {
              return user.lastName.toUpperCase();
            },
          ])
        : sortBy(tempPortalUsers, [
            (user: IPortalUser) => {
              return user.lastName.toUpperCase();
            },
          ]).reverse();
      return tempPortalUsers;
    }
    return portalUsers;
  };

  const GetPortalUsers = async (agencyCode: string, isAuthorized: boolean = false, fromCache: boolean = true) => {
    try {
      setIsLoadingData(true);
      const params: any = { 
        agencyCode: agencyCode,
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
        useCache: fromCache
      };
      
      // Add filters to params
      Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (Array.isArray(value)) {
          params[`filter_${key}`] = value.join(',');
        } else if (value) {
          params[`filter_${key}`] = value;
        }
      });
      
      const response = await fetch('/api/sitecore/AgencyAPIAsync/GetBLPortalUsersPaginated', {
        method: 'POST',
        body: new URLSearchParams(params),
      });
      let result = await response.json();
      
      // Check if response has new pagination format
      if (result.data !== undefined && result.pagination !== undefined) {
        // New server-side pagination format
        const hasFilters = Object.keys(filters).length > 0;
        const hasNoResults = result.pagination.totalRecords === 0;
        
        // Only set noUsersFound if there are no users AND no filters are active
        setNoUsersFound(hasNoResults && !hasFilters && agencyCode !== '');
        setHasLoadedAgency(true);
        setPortalUsers({ data: result.data, isLoaded: true, isAuthorized: result.isAuthorized });
        setTotalRecords(result.pagination.totalRecords);
        setIsLoadingData(false);
      } else {
        // Fallback to old format for backward compatibility
        let portalUsers: IManagePortalUsersWrapper = result;
        if (portalUsers.activeUsers && portalUsers.activeUsers.length > 0) {
          let activeUsers = SortPortalUsersByLastName(true, portalUsers.activeUsers);
          setNoUsersFound(false);
          setHasLoadedAgency(true);
          setPortalUsers({ data: activeUsers, isLoaded: true, isAuthorized: portalUsers.isAuthorized });
          setTotalRecords(activeUsers.length);
          setIsLoadingData(false);
        } else {
          const hasFilters = Object.keys(filters).length > 0;
          if (!agencyCode) {
            //set authorization for employees on load, isLoaded should be true if the authorization is false
            setPortalUsers({ data: [], isLoaded: !portalUsers.isAuthorized, isAuthorized: portalUsers.isAuthorized });
          } else {
            // Only set noUsersFound if no filters are active
            setNoUsersFound(!hasFilters);
            setHasLoadedAgency(true);
            setPortalUsers({ data: [], isLoaded: true, isAuthorized: portalUsers.isAuthorized });
          }
          setTotalRecords(0);
          setIsLoadingData(false);
        }
      }
    } catch (error) {
      //no users or server error
      setPortalUsers({ data: [], isLoaded: true, isAuthorized: isAuthorized });
      setNoUsersFound(true);
      setTotalRecords(0);
      setIsLoadingData(false);
    }
  };

  const GetAgencyName = async (agencyCode: string) => {
    if (agencyCode) {
      let agencyInfo: IAgencyInfo = await GetAgencyInfo(agencyCode);
      if (agencyInfo && agencyInfo.AgencyName) {
        setAgencyName(agencyInfo.AgencyName);
      }
    }
  };

  const DisableUser = async (userToDisable: string) => {
    //make a call somewhere to disable that user, then call the get users endpoint again
    setConfirmationModalIsOpen(false);
    setIsDisabling(true);
    const response = await fetch('/api/sitecore/AgencyAPIAsync/DisableUser', {
      method: 'POST',
      body: new URLSearchParams({ userId: userToDisable, agencyCode: agencyCode || '' }),
    });
    if (!response.ok) {
      let error = await response.text();
      console.error(`ERROR - ${JSON.stringify(error)}\n`);
      setNotifications((notifications) => [
        ...notifications,
        {
          message: `User update failed. Please try again later.`,
          highlightType: 'negative',
          index: notifications.length,
        },
      ]);
      setIsDisabling(false);
    } else {
      setNotifications((notifications) => [
        ...notifications,
        {
          message: `User ${user} has been successfully disabled.`,
          highlightType: 'positive',
          index: notifications.length,
        },
      ]);
      setPortalUsers((portalUsers) => ({
        ...portalUsers,
        data: portalUsers.data?.filter((x) => x.userId !== user),
      }));
      setIsDisabling(false);
    }
    if (componentTopDiv.current) componentTopDiv.current.scrollIntoView();
  };

  const UpdateRole = async (userToUpdate: string, role: string) => {
    setIsUpdating(true);
    setUser(userToUpdate);
    const response = await fetch('/api/sitecore/AgencyAPIAsync/UpdateRole', {
      method: 'POST',
      body: new URLSearchParams({ userToUpdate: userToUpdate, role: role, agencyCode: agencyCode || '' }),
    });
    if (!response.ok) {
      let error = await response.text();
      console.error(`ERROR - ${JSON.stringify(error)}\n`);
      setIsUpdating(false);
      setNotifications((notifications) => [
        ...notifications,
        {
          message: `User update failed. Please try again later.`,
          highlightType: 'negative',
          index: notifications.length,
        },
      ]);
    } else {
      let data = await response.json();
      setIsUpdating(false);
      if (data.success) {
        setNotifications((notifications) => [
          ...notifications,
          {
            message: `User ${userToUpdate} has been successfully updated.`,
            highlightType: 'positive',
            index: notifications.length,
          },
        ]);
        setPortalUsers((portalUsers) => ({
          ...portalUsers,
          data: portalUsers.data?.map((pUser) => (pUser.userId === userToUpdate ? { ...pUser, role: role } : pUser)),
        }));
      } else
        setNotifications((notifications) => [
          ...notifications,
          {
            message: `User update failed. Please try again later.`,
            highlightType: 'negative',
            index: notifications.length,
          },
        ]);
    }
    if (componentTopDiv.current) componentTopDiv.current.scrollIntoView();
  };

  const InitializeUser = async (userToInitialize: string | undefined) => {
    if (userToInitialize) {
      try {
        const response = await fetch('/api/sitecore/AgencyAPIAsync/InitializeBLPortalUser', {
          method: 'POST',
          body: new URLSearchParams({ userID: userToInitialize }),
        });

        if (!response.ok) {
          let error = await response.text();
          console.error(
            `ERROR in ManageBLPortalUsers.InitializeUser(): response?.ok: ${response?.ok} | response?.status: ${response?.status} | response.text(): ${JSON.stringify(
              error,
            )}`,
          );
        } else {
          let result = await response.json();
          let initializeUserResponse: IInitializePortalUser = result;
          if (initializeUserResponse?.success && searchAgencyCode) {
            setInitializeUserIDError(false);
            GetPortalUsers(searchAgencyCode, false, false);
            GetAgencyName(searchAgencyCode);
          } else {
            setInitializeUserIDError(true);
            console.error(
              `ERROR in ManageBLPortalUsers.InitializeUser(): success: ${JSON.stringify(
                initializeUserResponse?.success,
              )}\n - errMessage: ${JSON.stringify(initializeUserResponse?.errMessage)}`,
            );
          }
        }
      } catch (err: any) {
        console.error(`ERROR in ManageBLPortalUsers.InitializeUser(): ${err}\n - ${err.message}`);
        setInitializeUserIDError(true);
        setPortalUsers({ data: [], isLoaded: true, isAuthorized: portalUsers?.isAuthorized });
      }
    }
  };

  const isValidAgencyCode = (code: string): boolean => {
    var isValid = code !== undefined && validAgencyCode.test(code);
    setIsValidAgencySearch(isValid);
    return isValid;
  };

  const SearchAgencyPortalUsers = () => {
    setNotifications([]);
    setHasPerformedSearch(true);
    if (searchAgencyCode && isValidAgencyCode(searchAgencyCode)) {
      setAgencyCode(searchAgencyCode);
    }
  };

  const RefreshAgencyData = () => {
    setNotifications([]);
    if (agencyCode) {
      setPagination({ page: 1, pageSize: 25 });
      setFilters({});
      setHasLoadedAgency(false);
      GetPortalUsers(agencyCode, portalUsers.isAuthorized, false);
    }
  };

  const handleDisableUserClick = (userId: string) => {
    setConfirmationModalIsOpen(true);
    setUser(userId);
  };

  const exportData = async (agencyName: string | undefined, agencyNumber: string | undefined) => {
    // Fetch all records for export (without pagination or filters)
    try {
      const params: any = { 
        agencyCode: agencyNumber || '',
        page: 1,
        pageSize: 999999, // Request all records
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      };
      
      const response = await fetch('/api/sitecore/AgencyAPIAsync/GetBLPortalUsersPaginated', {
        method: 'POST',
        body: new URLSearchParams(params),
      });
      const result = await response.json();
      
      // Use data from pagination format
      const dataToExport = result.data || [];
      
      exportUtility.ExportData(dataToExport, agencyName, agencyNumber);
    } catch (error) {
      console.error('Error fetching data for export:', error);
      // Fallback to current page data if fetch fails
      exportUtility.ExportData(portalUsers?.data, agencyName, agencyNumber);
    }
  };

  const isAgentExperience = () => {
    return userData?.isAgent() || userData?.isMockingAgent;
  };

  const searchOnEnter = (key: string) => {
    if (key.toLowerCase() === 'enter') {
      SearchAgencyPortalUsers();
    }
  };

  const updateSearchAgencyCode = (search: string) => {
    setSearchAgencyCode(search);
    if (search && search.length > AgencyCodeLength - 1) {
      isValidAgencyCode(search);
    } else if (search === '') {
      setIsValidAgencySearch(true);
    }
  };

  return portalUsers?.isAuthorized && userData?.selectedStatCodeData?.SubStat ? (
    <>
      <div className="agency-info-search" id="manage-portal-users-agency-info" ref={componentTopDiv}>
        {!isAgentExperience() && (
          <>
            <div className="agency-portal-users-search">
              <FieldGroup
                messages={
                  isValidAgencySearch && !noUsersFound
                    ? []
                    : noUsersFound
                      ? [{ text: `No users found for agency ${agencyCode}.`, type: 'error' }]
                      : [{ text: 'The agency code should be a 7 digit number', type: 'error' }]
                }
              >
                <SearchInput
                  highlightType={isValidAgencySearch ? undefined : 'error'}
                  labelVisual={'Agency code'}
                  value={searchAgencyCode}
                  onChange={(e) => updateSearchAgencyCode(e.target.value)}
                  onKeyDown={(e) => searchOnEnter(e.key)}
                  onClearClicked={() => updateSearchAgencyCode('')}
                />
              </FieldGroup>
              <Button
                onClick={SearchAgencyPortalUsers}
                size="small"
                variant="secondary"
                className="agency-users-search"
              >
                View agency
              </Button>
              {agencyCode && portalUserCount > 0 && (
                <Button
                  onClick={RefreshAgencyData}
                  size="small"
                  variant="secondary"
                  className="agency-users-search"
                >
                  <IconRefresh size="16" /> Refresh
                </Button>
              )}
            </div>
            {noUsersFound && hasPerformedSearch ? (
              <>
                <div className="no-users-text">Let's try to look up the agency using one of its users.</div>
                <div className="agency-portal-users-search">
                  <Form onSubmit={() => InitializeUser(initializeUserID)}>
                    <FieldGroup
                      messages={[
                        !initializeUserIDError
                          ? {}
                          : {
                              text: 'We could not find that user ID. Try another user ID or view a different agency.',
                              type: 'error',
                            },
                      ]}
                    >
                      <SearchInput
                        highlightType={!initializeUserID ? undefined : 'error'}
                        labelVisual={'User ID'}
                        value={initializeUserID}
                        onBlur={(e) => {
                          setInitializeUserID(e.target.value);
                        }}
                        onClearClicked={() => {
                          setInitializeUserID('');
                          setInitializeUserIDError(false);
                        }}
                      />
                    </FieldGroup>
                    <Button
                      onClick={() => InitializeUser(initializeUserID)}
                      size="small"
                      variant="secondary"
                      className="agency-users-search"
                    >
                      View user's agency
                    </Button>
                  </Form>
                </div>
              </>
            ) : (
              <></>
            )}
          </>
        )}
        {userData && (
          <div className="agency-info">
            {agencyCode && portalUserCount > 0 && (
              <>
                <Heading type="h6-small">
                  <strong>Agency name: </strong>
                  {portalUserCount > 0 && agencyName}
                </Heading>
                <Heading type="h6-small">
                  <strong>Agency number: </strong>
                  {agencyCode}
                </Heading>
              </>
            )}
          </div>
        )}
      </div>
      <div className="manage-portal-users-data">
        {portalUsers.isLoaded && hasLoadedAgency && !noUsersFound && agencyCode ? (
          <>
            <div className="create-user-export-data">
              <CreatePortalUser agencyCode={agencyCode}></CreatePortalUser>
              <div className="export-button" onClick={() => exportData(agencyName, agencyCode)} tabIndex={0}>
                <IconSaveModern size="16" /> Export
              </div>
            </div>
            {notifications &&
              notifications.length > 0 &&
              notifications
                .sort((a, b) => {
                  return b.index - a.index;
                })
                .map((notification) => (
                  <Notification
                    onDismiss={() =>
                      setNotifications((notifications) => notifications.filter((x) => x.index !== notification.index))
                    }
                    highlightType={notification.highlightType}
                    alert={notification.message}
                    isDismissible
                  />
                ))}
            <PortalUsersTable
              portalUsers={portalUsers.data || []}
              isUpdating={isUpdating}
              isDisabling={isDisabling}
              user={user}
              onUpdateRole={UpdateRole}
              onDisableUser={handleDisableUserClick}
              pagination={pagination}
              totalRecords={totalRecords}
              sorting={sorting}
              filters={filters}
              onPaginationChange={setPagination}
              onSortingChange={setSorting}
              onFiltersChange={setFilters}
              isLoadingData={isLoadingData}
            />
          </>
        ) : !portalUsers.isLoaded && !isLoadingData ? (
          agencyCode && (
            <CircularProgressSpinner
              className="portal-users-loading-spinner"
              size="32"
              aria-label="Loading portal users data..."
            />
          )
        ) : (
          <></>
        )}
      </div>
      <Modal
        isOpen={confirmationModalIsOpen}
        onClose={() => setConfirmationModalIsOpen(false)}
        size="small"
        className="disable-user-modal"
      >
        <ModalBody>
          <Heading tag="span" type="h3-light">
            Are you sure you want to disable this user's profile?
          </Heading>
        </ModalBody>
        <ModalFooter>
          <GridRow gutterSize="fixed-16" gutters>
            <GridCol base={12} md={6}>
              <Button dynamicWidth onClick={() => setConfirmationModalIsOpen(false)}>
                Cancel
              </Button>
            </GridCol>
            <GridCol base={12} md={6}>
              <Button dynamicWidth variant="primary" onClick={() => user && DisableUser(user)}>
                Yes, disable
              </Button>
            </GridCol>
          </GridRow>
        </ModalFooter>
      </Modal>
    </>
  ) : portalUsers.isLoaded && userData?.selectedStatCodeData?.SubStat ? (
    <div className="no-authorization">
      It looks like you are not authorized to access this tool. For access, please contact your agency administrator.
      For additional questions, contact Agency Interface Support at (888) 451-8414.
    </div>
  ) : userData?.selectedStatCodeData?.SubStat ? (
    <CircularProgressSpinner
      className="portal-authorization-loading-spinner"
      size="32"
      aria-label="Loading manage portal users..."
    />
  ) : (
    <>Error: User agency number data is missing.</>
  );
}

export default ManageBLPortalUsers;
